import { forwardRef, useRef } from 'react';
import { ButtonDropdown, ButtonV2 } from '@cimplico/components';
import Editor, { EditorRef } from '../rich-text-editor/Editor';

export type CreateReplyProps = {
    rawContent: string;
    structuredContent: string;
};

type NewReplyProps = {
    /**
     * Reply value
     */
    reply: { rawContent: string; structuredContent: string };
    /**
     * Whether reply editor is currently focused
     */
    replyEditorFocused: boolean;
    /**
     * Whether create query is loading
     */
    isCreating: boolean;
    /**
     * Additonal actions to show in button dropdown
     */
    extraActions?: { label: string; onClick: (params: CreateReplyProps) => void }[];
    /**
     * Callback when new reply content changes
     */
    onNewReply: (reply: { rawContent: string; structuredContent: string }) => void;
    /**
     * Callback when new reply is being created
     */
    onCreate: (params: CreateReplyProps) => void;
    /**
     * Callback when reply editor focused state changes
     */
    onReplyEditorFocused: (isFocused: boolean) => void;
};

/**
 * New reply section displayed after current replies content
 */
const NewReply = forwardRef(function NewReply(
    { reply, replyEditorFocused, isCreating, extraActions, onNewReply, onCreate, onReplyEditorFocused }: NewReplyProps,
    ref: React.ForwardedRef<EditorRef>
): JSX.Element {
    return (
        <div className="flex flex-col justify-center gap-1 mt-2">
            <div className={`${replyEditorFocused ? 'h-[150px]' : ''}`} onClick={(): void => onReplyEditorFocused(true)}>
                <Editor
                    hideToolbar={!replyEditorFocused}
                    placeholder="Add a reply..."
                    onContentChange={(raw, structured): void => {
                        onNewReply({ rawContent: raw, structuredContent: structured });
                    }}
                    ref={ref}
                    autoFocus={false}
                />
            </div>
            {replyEditorFocused && (
                <div className="flex items-center gap-2 justify-end">
                    <ButtonV2
                        size="xs"
                        type="hollow"
                        label="Cancel"
                        onClick={(): void => onReplyEditorFocused(false)}
                        isDisabled={isCreating}
                    />
                    {extraActions && extraActions.length > 0 ? (
                        <ButtonDropdown
                            size="xs"
                            label="Save"
                            variant="primary"
                            disabled={isCreating || !reply.rawContent}
                            isLoading={isCreating}
                            onClick={(): void => onCreate({ rawContent: reply.rawContent, structuredContent: reply.structuredContent })}
                        >
                            {extraActions.map((action, idx) => (
                                <ButtonDropdown.SecondaryAction
                                    key={idx}
                                    label={action.label}
                                    disabled={isCreating || !reply.rawContent}
                                    onClick={(): void =>
                                        action.onClick({ rawContent: reply.rawContent, structuredContent: reply.structuredContent })
                                    }
                                />
                            ))}
                        </ButtonDropdown>
                    ) : (
                        <ButtonV2
                            size="xs"
                            label="Save"
                            type="primary"
                            onClick={(): void => onCreate({ rawContent: reply.rawContent, structuredContent: reply.structuredContent })}
                            isLoading={isCreating}
                            isDisabled={isCreating || !reply.rawContent}
                        />
                    )}
                </div>
            )}
        </div>
    );
});

export default NewReply;
